import { defineStore } from 'pinia';
import axios from 'axios';
import useAuthStore from '~/stores/auth';
import {
   defaultResponseStatus,
   handleApiRequest,
} from '~/functions/handleApiRequest';
import type { ResponseStatus } from '~/lms';

interface BillingStoreState {
   lmsApiBaseurl: string;
   /**
    * Whether we sent a requests to backend to generate a checkout session for the user
    */
   isCreatingCheckoutSessionForPlanId: string;
   isCreatingBillingPortalUrl: boolean;
   stripePortalResponseStatus: ResponseStatus;
   billingSessionResponseStatus: ResponseStatus;
   billingPortalUrl: string;
   isVerifyingPayment: boolean;
   paymentVerificationStatus: ResponseStatus;
}

interface BillingStoreActions {
   initiateCheckoutSession: (planId: string, cancelUrl: string) => void;
   getBillingPortalUrl: () => void;
   verifyPaymentSession: (sessionId: string) => Promise<any>;
   verifyPaymentSessionWithPolling: (
      sessionId: string,
      maxAttempts?: number,
      initialDelay?: number,
   ) => Promise<any>;
}

interface BillingStoreGetters {}

const useBillingStore = defineStore<
   string,
   BillingStoreState,
   {},
   BillingStoreActions
>('billing', {
   state: (): BillingStoreState => {
      return {
         lmsApiBaseurl: '',
         isCreatingBillingPortalUrl: false,
         isCreatingCheckoutSessionForPlanId: '',
         billingPortalUrl: '',
         stripePortalResponseStatus: defaultResponseStatus,
         billingSessionResponseStatus: defaultResponseStatus,
         isVerifyingPayment: false,
         paymentVerificationStatus: defaultResponseStatus,
      };
   },
   actions: {
      async initiateCheckoutSession(planId, cancelUrl) {
         if (!cancelUrl || cancelUrl.trim() === '') {
            throw new Error('cancelUrl is required for checkout session');
         }

         this.isCreatingCheckoutSessionForPlanId = planId;
         const result = await handleApiRequest(
            this,
            undefined,
            'billingSessionResponseStatus',
            () =>
               axios.get(`${this.lmsApiBaseurl}checkout-session`, {
                  headers: useAuthStore().authHeaders,
                  params: {
                     planId,
                     cancelUrl,
                  },
               }),
         );

         if (result && result.checkoutUrl) {
            window.location.href = result.checkoutUrl;
         }
      },
      async getBillingPortalUrl() {
         const result = await handleApiRequest(
            this,
            'isCreatingBillingPortalUrl',
            'stripePortalResponseStatus',
            () =>
               axios.get(`${this.lmsApiBaseurl}billing-portal-session`, {
                  headers: useAuthStore().authHeaders,
               }),

            { ref: 'billingPortalUrl', value: undefined },
         );

         if (!result) {
            return;
         }

         window.location.href = result.billingPortalUrl;
      },
      async verifyPaymentSession(sessionId: string) {
         const result = await handleApiRequest(
            this,
            'isVerifyingPayment',
            'paymentVerificationStatus',
            () =>
               axios.get(`${this.lmsApiBaseurl}payment/session-info`, {
                  headers: useAuthStore().authHeaders,
                  params: { session_id: sessionId },
               }),
         );

         return result;
      },
      async verifyPaymentSessionWithPolling(
         sessionId: string,
         maxAttempts: number = 20,
         initialDelay: number = 1000,
      ) {
         let attempt = 0;
         let delay = initialDelay;

         while (attempt < maxAttempts) {
            attempt++;

            const result = await this.verifyPaymentSession(sessionId);

            // Success case - payment verified
            if (result?.success && result?.payment_status === 'paid') {
               return result;
            }

            // If we get a 404 or payment incomplete, continue polling
            const statusCode = this.paymentVerificationStatus.statusCode;
            if (
               statusCode === 404 ||
               (result && !result.success && result.payment_status !== 'paid')
            ) {
               if (attempt < maxAttempts) {
                  await new Promise((resolve) => setTimeout(resolve, delay));
                  // Exponential backoff with jitter
                  delay = Math.min(delay * 1.5 + Math.random() * 1000, 10000);
                  continue;
               }
            }

            // Other errors (401, 500, etc.) - don't retry
            if (statusCode && statusCode >= 400 && statusCode !== 404) {
               return result;
            }
         }

         // Max attempts reached
         this.paymentVerificationStatus = {
            statusCode: 408,
            message:
               'Payment verification timed out. Please check your payment status or contact support.',
         };

         return null;
      },
   },
});

export default useBillingStore;
